import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  UploadCloud, FileSpreadsheet, ArrowRight, CheckCircle2, AlertTriangle,
  Trash2, Database, RotateCcw,
} from 'lucide-react'
import { salesHistoryService } from '../services/api'
import { Spinner } from '../components/ui'

// Fields we need, and header keywords we auto-match against the CSV columns.
const FIELDS = [
  { key: 'date',          label: 'Sale date',          required: true,  hint: 'transaction / invoice date',      match: ['date', 'txn', 'invoice', 'order', 'day', 'trans'] },
  { key: 'stock_code',    label: 'Stock code',         required: true,  hint: 'matches a product stock code',    match: ['stock', 'code', 'item', 'sku', 'product', 'barcode'] },
  { key: 'quantity',      label: 'Quantity',           required: true,  hint: 'units sold',                      match: ['qty', 'quantity', 'units', 'sold', 'pieces'] },
  { key: 'unit_price',    label: 'Unit price',         required: false, hint: 'optional — used for revenue',     match: ['price', 'unit', 'rate', 'cost'] },
  { key: 'line_total',    label: 'Line total',         required: false, hint: 'optional — overrides price × qty', match: ['total', 'amount', 'value', 'linetotal', 'nett', 'net'] },
  { key: 'order_ref',     label: 'Order / invoice no.', required: false, hint: 'groups lines into one order',     match: ['invoice', 'doc', 'ref', 'order', 'number', 'no'] },
  { key: 'customer_code', label: 'Customer code',      required: false, hint: 'optional — links to a customer',  match: ['customer', 'account', 'acc', 'client', 'debtor'] },
]

const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '')

function autoGuess(columns) {
  const map = {}
  const used = new Set()
  for (const f of FIELDS) {
    // best column whose normalised header contains one of the field's keywords
    const hit = columns.find(c => {
      if (used.has(c)) return false
      const n = norm(c)
      return f.match.some(k => n.includes(k))
    })
    if (hit) { map[f.key] = hit; used.add(hit) }
  }
  return map
}

export default function SalesHistory() {
  const qc = useQueryClient()
  const [file, setFile] = useState(null)
  const [columns, setColumns] = useState([])
  const [samples, setSamples] = useState([])
  const [totalRows, setTotalRows] = useState(0)
  const [mapping, setMapping] = useState({})
  const [dateFormat, setDateFormat] = useState('dmy')
  const [summary, setSummary] = useState(null)

  const { data: batches } = useQuery({
    queryKey: ['sales-history', 'batches'],
    queryFn: () => salesHistoryService.batches(),
    staleTime: 30 * 1000,
  })
  const batchList = Array.isArray(batches) ? batches : []

  const inspectMut = useMutation({
    mutationFn: (f) => salesHistoryService.inspect(f),
    onSuccess: (res) => {
      setColumns(res.columns || [])
      setSamples(res.sample_rows || [])
      setTotalRows(res.total_rows || 0)
      setMapping(autoGuess(res.columns || []))
      setSummary(null)
    },
    onError: () => toast.error('Could not read that file — is it a CSV?'),
  })

  const importMut = useMutation({
    mutationFn: () => salesHistoryService.import(file, mapping, dateFormat),
    onSuccess: (res) => {
      setSummary(res)
      qc.invalidateQueries(['sales-history', 'batches'])
      toast.success(`Imported ${res.orders_created} orders (${res.items_created} lines)`)
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Import failed'),
  })

  const deleteMut = useMutation({
    mutationFn: (id) => salesHistoryService.deleteBatch(id),
    onSuccess: (res) => {
      qc.invalidateQueries(['sales-history', 'batches'])
      toast.success(`Removed ${res.deleted_orders} imported orders`)
    },
    onError: () => toast.error('Could not remove that batch'),
  })

  const onPick = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    setFile(f)
    inspectMut.mutate(f)
  }

  const reset = () => {
    setFile(null); setColumns([]); setSamples([]); setTotalRows(0)
    setMapping({}); setSummary(null)
  }

  const missingRequired = useMemo(
    () => FIELDS.filter(f => f.required && !mapping[f.key]).map(f => f.label),
    [mapping],
  )

  const inputCls = 'w-full bg-black/40 border border-[#323232] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-primary/60'
  const labelCls = 'text-xs font-semibold text-gray-400 uppercase tracking-wide'

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-bold text-white flex items-center gap-2">
          <Database size={20} className="text-primary" /> Import Sales History
        </h1>
        <p className="text-sm text-gray-400 mt-1">
          Upload a sales export from your POS as a CSV. Map its columns to the fields below, and we'll load
          the history as dated orders so the forecasting engine can train on your real demand.
        </p>
      </div>

      {/* Step 1 — upload */}
      <div className="bg-[#161616] border border-[#282828] rounded-2xl p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-primary/15 text-primary text-xs flex items-center justify-center font-bold">1</span>
            Choose your CSV
          </h2>
          {file && (
            <button onClick={reset} className="text-xs text-gray-500 hover:text-red-400 flex items-center gap-1">
              <RotateCcw size={12} /> Start over
            </button>
          )}
        </div>

        {!file ? (
          <label className="mt-3 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#323232] rounded-xl py-10 cursor-pointer hover:border-primary/50 transition-colors">
            <UploadCloud size={28} className="text-gray-500" />
            <span className="text-sm text-gray-400">Click to choose a POS export (.csv)</span>
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={onPick} />
          </label>
        ) : (
          <div className="mt-3 flex items-center gap-3 text-sm text-gray-300">
            <FileSpreadsheet size={18} className="text-primary" />
            <span className="font-medium">{file.name}</span>
            {inspectMut.isPending
              ? <Spinner size={14} />
              : <span className="text-gray-500">· {columns.length} columns · {totalRows.toLocaleString()} rows</span>}
          </div>
        )}
      </div>

      {/* Step 2 — mapping */}
      {columns.length > 0 && (
        <div className="bg-[#161616] border border-[#282828] rounded-2xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-primary/15 text-primary text-xs flex items-center justify-center font-bold">2</span>
            Map your columns
          </h2>
          <p className="text-xs text-gray-500">
            We've guessed these from your headers — adjust anything that's off. Fields marked * are required.
          </p>

          <div className="grid sm:grid-cols-2 gap-3">
            {FIELDS.map(f => (
              <div key={f.key}>
                <label className={labelCls}>
                  {f.label}{f.required && <span className="text-red-400"> *</span>}
                </label>
                <div className="flex items-center gap-2 mt-1">
                  <ArrowRight size={14} className="text-gray-600 flex-shrink-0" />
                  <select className={inputCls} value={mapping[f.key] || ''}
                    onChange={e => setMapping(m => ({ ...m, [f.key]: e.target.value || undefined }))}>
                    <option value="">— not in my file —</option>
                    {columns.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <p className="text-[11px] text-gray-600 mt-0.5 ml-6">{f.hint}</p>
              </div>
            ))}
          </div>

          <div>
            <label className={labelCls}>Date format in your file</label>
            <div className="flex gap-2 mt-1">
              {[{ v: 'dmy', label: 'Day / Month / Year' }, { v: 'mdy', label: 'Month / Day / Year' }].map(o => (
                <button key={o.v} type="button" onClick={() => setDateFormat(o.v)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                    dateFormat === o.v ? 'bg-primary/10 border-primary/50 text-primary'
                                       : 'bg-black/40 border-[#323232] text-gray-400 hover:text-gray-200'}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Sample preview */}
          {samples.length > 0 && mapping.date && (
            <div className="overflow-x-auto rounded-lg border border-[#282828]">
              <table className="w-full text-xs">
                <thead className="bg-black/40 text-gray-400">
                  <tr>
                    {FIELDS.filter(f => mapping[f.key]).map(f => (
                      <th key={f.key} className="px-3 py-2 text-left font-semibold">{f.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {samples.map((r, i) => (
                    <tr key={i} className="border-t border-[#222]">
                      {FIELDS.filter(f => mapping[f.key]).map(f => (
                        <td key={f.key} className="px-3 py-1.5 text-gray-300 whitespace-nowrap">{r[mapping[f.key]]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={() => importMut.mutate()}
              disabled={missingRequired.length > 0 || importMut.isPending}
              className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {importMut.isPending ? <Spinner size={14} /> : <UploadCloud size={15} />}
              Import {totalRows.toLocaleString()} rows
            </button>
            {missingRequired.length > 0 && (
              <span className="text-xs text-amber-400 flex items-center gap-1">
                <AlertTriangle size={13} /> Map: {missingRequired.join(', ')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div className="bg-[#161616] border border-primary/30 rounded-2xl p-5">
          <h2 className="text-sm font-semibold text-primary flex items-center gap-2">
            <CheckCircle2 size={16} /> Import complete
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
            <Stat label="Orders created" value={summary.orders_created} accent />
            <Stat label="Line items" value={summary.items_created} />
            <Stat label="From" value={summary.date_from || '—'} />
            <Stat label="To" value={summary.date_to || '—'} />
          </div>
          {(summary.skipped_unmatched_product > 0 || summary.skipped_no_date > 0 || summary.skipped_bad_quantity > 0) && (
            <div className="mt-3 text-xs text-gray-400 space-y-1">
              {summary.skipped_unmatched_product > 0 && (
                <p>· {summary.skipped_unmatched_product.toLocaleString()} lines skipped — stock code not in the catalogue
                  {summary.unmatched_stock_codes?.length > 0 && (
                    <span className="text-gray-600"> (e.g. {summary.unmatched_stock_codes.slice(0, 8).join(', ')})</span>
                  )}
                </p>
              )}
              {summary.skipped_no_date > 0 && <p>· {summary.skipped_no_date.toLocaleString()} lines skipped — unreadable date</p>}
              {summary.skipped_bad_quantity > 0 && <p>· {summary.skipped_bad_quantity.toLocaleString()} lines skipped — zero/invalid quantity</p>}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-3">Head to Forecasting — the engine now includes this history.</p>
        </div>
      )}

      {/* Existing batches */}
      {batchList.length > 0 && (
        <div className="bg-[#161616] border border-[#282828] rounded-2xl p-5">
          <h2 className="text-sm font-semibold text-white mb-3">Imported batches</h2>
          <div className="space-y-2">
            {batchList.map(b => (
              <div key={b.batch_id} className="flex items-center justify-between bg-black/30 border border-[#282828] rounded-lg px-3 py-2 text-sm">
                <div className="text-gray-300">
                  <span className="font-mono text-xs text-gray-500">{b.batch_id}</span>
                  <span className="mx-2 text-gray-600">·</span>
                  {b.orders.toLocaleString()} orders
                  <span className="text-gray-600"> · {b.date_from} → {b.date_to}</span>
                </div>
                <button onClick={() => { if (confirm('Remove this imported batch?')) deleteMut.mutate(b.batch_id) }}
                  className="text-gray-500 hover:text-red-400 flex items-center gap-1 text-xs">
                  <Trash2 size={13} /> Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-black/30 border border-[#282828] rounded-lg px-3 py-2">
      <div className={`text-lg font-bold ${accent ? 'text-primary' : 'text-white'}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</div>
    </div>
  )
}
